import { useEffect, useState } from 'react';

// First-party cookie / ad-consent banner, wired to Google Consent
// Mode v2 (the default-denied stub lives in index.html). This is the
// compliant baseline AdSense expects before serving ads to EEA/UK
// visitors:
//   • Accept → grant ad_storage / ad_user_data / ad_personalization
//     so AdSense can serve personalized ads.
//   • Reject → leave them denied; AdSense still serves, but
//     non-personalized. No revenue cliff, just no personalization.
// The choice is remembered in localStorage so the banner shows once.
//
// Note: for full IAB TCF v2.2 certification in the EEA you can later
// switch on Google's own certified GDPR message in AdSense → Privacy
// & messaging; it rides on the same adsbygoogle loader. This banner +
// Consent Mode is the in-app baseline and does not conflict with it.

const STORAGE_KEY = 'og_consent_v1';

function applyConsent(granted) {
  if (typeof window.gtag !== 'function') return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value
  });
}

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* private mode */ }
    if (stored === 'granted' || stored === 'denied') {
      // Re-apply the saved choice on every load so Consent Mode
      // reflects it for this session's ad requests too.
      applyConsent(stored === 'granted');
      return;
    }
    setVisible(true);
  }, []);

  const choose = (granted) => {
    applyConsent(granted);
    try { localStorage.setItem(STORAGE_KEY, granted ? 'granted' : 'denied'); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="consent" role="dialog" aria-label="Cookie and ad consent" aria-live="polite">
      <div className="consent-inner">
        <p className="consent-copy">
          We use cookies to keep you signed in and to show ads that keep Outglow free.
          You can allow personalized ads, or choose essential-only — you’ll still see ads,
          just non-personalized. See our{' '}
          {/* Plain anchor, not a router link: /legal/* is served by the Express
              legalProxy, not by React Router, so a router link never left the SPA
              — it matched the catch-all and silently redirected to the landing
              page. That was the one link a GDPR-conscious visitor or an AdSense
              reviewer is most likely to click. Every other legal link in the app
              already uses a plain anchor. */}
          <a href="/legal/privacy" className="consent-link">privacy policy</a>.
        </p>
        <div className="consent-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => choose(false)}>Essential only</button>
          <button className="btn btn-primary btn-sm" onClick={() => choose(true)}>Allow all</button>
        </div>
      </div>
      <style>{`
        .consent {
          position: fixed; left: 0; right: 0; bottom: 0; z-index: 1000;
          padding: 14px 16px;
          background: rgba(14,11,22,.96);
          backdrop-filter: blur(12px);
          border-top: 1px solid var(--line);
        }
        .consent-inner {
          width: min(1100px, 100% - 8px); margin: 0 auto;
          display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
          justify-content: space-between;
        }
        .consent-copy { color: var(--text-dim); font-size: 13px; line-height: 1.5; margin: 0; flex: 1; min-width: 260px; }
        .consent-link { color: var(--primary); }
        .consent-actions { display: flex; gap: 8px; flex-shrink: 0; }
        @media (max-width: 540px) { .consent-actions { width: 100%; } .consent-actions .btn { flex: 1; } }
      `}</style>
    </div>
  );
}
